<script setup lang="ts">
import NamaIcon from '@/components/icons/NamaIcon.vue';
import {dashboardPresenterKey, DashboardWidgetProps} from '@/utils/presenters/interfaces.ts';
import {computed, inject, ref, toRef} from 'vue';
import {colors, Screen, type colorsRgba} from 'quasar';
import Translator from '@/utils/common/Translator.ts';
import LoginUtils from '@/utils/login/LoginUtils.ts';
import PlaceUtils from '@/utils/common/PlaceUtils.ts';
import ObjectChecker from '@/utils/common/ObjectChecker.ts';
import {useDashboardViewOptionsStore} from '@/stores/dashboardViewOptionsStore.ts';
import BIActionMenu from '@/components/bi/BIActionMenu.vue';
import {
  buildDrillDownByClick, buildDrillDownClick, dispatchWidgetClickAction, executeLink,
} from '@/components/bi/biInteractionHelpers.ts';
import {useBIFilterBadges} from '@/components/bi/useBIFilterBadges.ts';
import {useBIInteractionData} from '@/components/bi/useBIInteractionData.ts';
import type {
  BIDrillDownByClick,
  BIDrillDownByDimension,
  BIDrillDownClick,
  BIDrillDownPointValues,
  BIDrillDownTarget,
  BILinkPointValues,
  BILinkTarget,
  CrossFilterEmission,
} from '@/components/bi/biChartTypes.ts';
import WizardSlotSelectorButton from '@/components/bi/WizardSlotSelectorButton.vue';
import MiniSparkline from '@/components/dashboardWidgets/MiniSparkline.vue';
import type {
  EnhancedCardData,
  EnhancedCardItem,
  EnhancedCardLayout,
  EnhancedCardStyle,
} from '@/components/dashboardWidgets/enhancedMetricsCardTypes';
import {
  parseColor,
  themeSafeBackground,
  themeSafeForeground,
  themeTextColor,
  widgetSurfaceColor,
} from "@/components/bi/themeSafeColors.ts";
import BIWidgetRuntimeSelectorInfo = namaTypes.BIWidgetRuntimeSelectorInfo;

const props = defineProps<DashboardWidgetProps>();
const emit = defineEmits<{
  crossFilterClicked: [emissions: CrossFilterEmission[]];
  drillDownRequested: [click: BIDrillDownClick];
  drillDownByRequested: [click: BIDrillDownByClick];
}>();

const widgetId: string = (props.chartLine.widgetId ?? 'enhanced-metrics-card');
const heading = computed(() => Translator.calcByLanguage(props.chartLine.arChartTitle, props.chartLine.enChartTitle));

const dashboardPresenter = inject(dashboardPresenterKey, undefined);
const runtimeSelectorInfo = computed<BIWidgetRuntimeSelectorInfo | undefined>(
  () => (props.chartLine as any)?.runtimeSelectorInfo);

async function onSlotOverrideChanged() {
  if (props.chartLine.widgetId)
    await dashboardPresenter?.redrawWidgets(props.chartLine.widgetId);
}

const enhancedCardData = computed<EnhancedCardData | undefined>(
  () => (props.chartLine as any)?.enhancedCardData as EnhancedCardData | undefined,
);
const cards = computed<EnhancedCardItem[]>(() => enhancedCardData.value?.cards ?? []);
const layout = computed<EnhancedCardLayout>(() => enhancedCardData.value?.cardLayout ?? {});
const stylesPalette = computed<EnhancedCardStyle[]>(() => enhancedCardData.value?.styles ?? []);

const containerStyle = computed(() => {
  const direction = layout.value.direction ?? 'row';
  const gap = layout.value.gap ?? 8;
  const minWidth = layout.value.minCardWidth ?? 220;
  const columnsPerRow = layout.value.columnsPerRow;
  if (!Screen.lt.sm && (direction === 'grid' || columnsPerRow)) {
    const cols = columnsPerRow ? `repeat(${columnsPerRow}, minmax(0, 1fr))`
      : `repeat(auto-fit, minmax(${minWidth}px, 1fr))`;
    return {display: 'grid', gridTemplateColumns: cols, gap: `${gap}px`};
  }
  return {display: 'flex', flexWrap: 'wrap' as const, gap: `${gap}px`};
});

// The card surface is deliberately a wash of the authored colour rather than a solid fill, so it
// composites over whichever theme background is behind it and never traps the card's text on a
// light fill in dark mode. cardWash() keeps that intent but resolves the colour properly: every CSS
// notation, and no background at all when none was authored. The card's own text colour, the value
// and the badge are absolute colours picked against a light card, so they go through
// themeSafeColors to stay readable once the same dashboard is opened in dark mode.
const CARD_WASH_ALPHA_PERCENT = 30;

// A card's own surface, and the theme text colour that sits on it - the pair the helpers need to
// judge whether an authored colour still reads on this card.
function paletteOf(card: EnhancedCardItem): EnhancedCardStyle | undefined {
  return card.cardStyleIndex !== undefined ? stylesPalette.value[card.cardStyleIndex] : undefined;
}

function surfaceUnder(card: EnhancedCardItem): colorsRgba | null {
  return cardSurfaceColor(paletteOf(card));
}

function textOnCard(card: EnhancedCardItem): colorsRgba | null {
  return parseColor(paletteOf(card)?.color) ?? themeTextColor();
}

function cardWash(bg: string | undefined): string | undefined {
  const rgb = parseColor(bg);
  if (!rgb) return bg;
  return colors.rgbToHex({r: rgb.r, g: rgb.g, b: rgb.b, a: CARD_WASH_ALPHA_PERCENT});
}

function cardStyleFor(card: EnhancedCardItem): Record<string, string | number> {
  const style: Record<string, string | number> = {};
  const palette = paletteOf(card);
  if (!palette) return style;
  const wash = cardWash(palette.bg);
  if (wash)
    style.backgroundColor = wash;
  const cardColor = themeSafeForeground(palette.color, cardSurfaceColor(palette));
  if (cardColor)
    style.color = cardColor;
  if (palette.borderColor)
    style.borderColor = palette.borderColor;
  if (palette.borderWidth !== undefined)
    style.borderWidth = palette.borderWidth;
  if (palette.borderRadius !== undefined)
    style.borderRadius = palette.borderRadius;
  if (palette.padding !== undefined)
    style.padding = palette.padding;
  if (palette.shadow && palette.shadow !== 'none')
    style.boxShadow = shadowFor(palette.shadow);
  return style;
}

// What a card's text sits on: the widget surface, plus the card's own wash when it has one.
function cardSurfaceColor(palette: EnhancedCardStyle | undefined): colorsRgba | null {
  const surface = widgetSurfaceColor();
  const wash = parseColor(palette?.bg);
  if (!surface || !wash) return surface;
  // Blend through hex strings: colors.blend reads alpha on its own 0-100 scale, not the 0-1 one
  // parseColor reports, and returns a string only for string operands.
  const washHex = colors.rgbToHex({r: wash.r, g: wash.g, b: wash.b, a: CARD_WASH_ALPHA_PERCENT});
  const surfaceHex = colors.rgbToHex({r: surface.r, g: surface.g, b: surface.b});
  return parseColor(colors.blend(washHex, surfaceHex));
}

function shadowFor(level: 'sm' | 'md' | 'lg'): string {
  switch (level) {
    case 'sm':
      return '0 1px 2px rgba(0,0,0,0.05)';
    case 'md':
      return '0 4px 6px rgba(0,0,0,0.07)';
    case 'lg':
      return '0 10px 15px rgba(0,0,0,0.1)';
    default:
      return 'none';
  }
}

function valueDisplay(card: EnhancedCardItem): string {
  const val = card.value;
  if (!val) return '';
  return val.dv ?? (val.v ?? '').toString();
}

function badgeDisplay(card: EnhancedCardItem): string {
  const val = card.badge;
  if (!val) return '';
  return val.dv ?? (val.v ?? '').toString();
}

function subtitleText(card: EnhancedCardItem): string {
  const sub = card.subtitle;
  if (!sub) return '';
  return Translator.calcByLanguage(sub.ar, sub.en) ?? '';
}

function headerText(card: EnhancedCardItem): string {
  return Translator.calcByLanguage(card.headerAr, card.headerEn) ?? '';
}

function valueStyle(card: EnhancedCardItem): Record<string, string> {
  const style: Record<string, string> = {};
  const v = card.value;
  if (!v) return style;
  const color = themeSafeForeground(v.color, surfaceUnder(card));
  if (color) style.color = color;
  if (v.fontSize) style.fontSize = v.fontSize.toString();
  return style;
}

function badgeStyle(card: EnhancedCardItem): Record<string, string> {
  const style: Record<string, string> = {};
  const b = card.badge;
  if (!b) return style;
  const bg = themeSafeBackground(b.bg, b.color, textOnCard(card));
  if (bg) style.backgroundColor = bg;
  const color = b.bg ? b.color : themeSafeForeground(b.color, surfaceUnder(card));
  if (color) style.color = color;
  return style;
}

function iconStyle(card: EnhancedCardItem): Record<string, string> {
  const style: Record<string, string> = {};
  const icon = card.icon;
  if (!icon) return style;
  const bg = themeSafeBackground(icon.bg, icon.color, textOnCard(card));
  if (bg) style.backgroundColor = bg;
  return style;
}

function iconColorStyle(card: EnhancedCardItem): Record<string, string> {
  const icon = card.icon;
  if (!icon?.color) return {};
  const color = icon.bg ? icon.color : themeSafeForeground(icon.color, surfaceUnder(card));
  return color ? {color} : {};
}

// --- BI interaction (click-emit, drill-down, links, drill-down-by) ---
const chartLineRef = toRef(props, 'chartLine');
const {
  interactionData, clickEmitData, drillTargets, drillFilterMetadata, drillPoints,
  linkTargets, linkPoints, drillByDimensions, drillByData,
} = useBIInteractionData(chartLineRef);

const appliedCrossFilterCodes = computed<string[]>(() => (props.chartLine as any).appliedCrossFilterCodes ?? []);
const {isFilterSource, isFilteredByOther, filteredByTooltip, emittingTooltip} =
  useBIFilterBadges(() => widgetId, appliedCrossFilterCodes);

const hasClickEmit = computed(() => clickEmitData.value.length > 0);
const hasClickAction = computed(() => {
  const action = interactionData.value?.clickAction;
  return !!action?.targetKey && action.type !== 'crossFilter';
});
const isCardClickable = computed(() => hasClickEmit.value || hasClickAction.value);
const hasMenu = computed(() =>
  drillTargets.value.length > 0 || linkTargets.value.length > 0 || drillByDimensions.value.length > 0,
);

function onCardClick(cardIdx: number, event: MouseEvent) {
  if (!isCardClickable.value) return;
  event.preventDefault();
  const dispatched = dispatchWidgetClickAction(interactionData.value?.clickAction, {
    drillTargets: drillTargets.value,
    linkTargets: linkTargets.value,
    linkPointValues: linkPoints.value[cardIdx],
    triggerDrillDown: target => triggerDrillDown(target, cardIdx),
  });
  if (dispatched || !hasClickEmit.value) return;
  const emissions: CrossFilterEmission[] = [];
  for (const mapping of clickEmitData.value) {
    const point = mapping.points?.[cardIdx];
    if (point && Object.keys(point).length > 0) {
      emissions.push({
        crossFilterCode: mapping.crossFilterCode,
        sourceChartId: mapping.sourceChartId ?? widgetId,
        dataIndex: cardIdx,
        ...point,
      });
    }
  }
  if (emissions.length) emit('crossFilterClicked', emissions);
}

interface MenuState {
  cardIdx: number;
  x: number;
  y: number;
}

const menu = ref<MenuState | undefined>();
const menuOpen = computed<boolean>({
  get: () => !!menu.value,
  set: (v) => { if (!v) menu.value = undefined; },
});

function openMenu(cardIdx: number, event: MouseEvent) {
  event.preventDefault();
  event.stopPropagation();
  if (!hasMenu.value) return;
  menu.value = {cardIdx, x: event.clientX, y: event.clientY};
}

const menuPointValues = computed<BIDrillDownPointValues>(
  () => (menu.value ? drillPoints.value[menu.value.cardIdx] : undefined) ?? {},
);
const menuLinkPointValues = computed<BILinkPointValues>(
  () => (menu.value ? linkPoints.value[menu.value.cardIdx] : undefined) ?? {},
);

function triggerDrillDown(target: BIDrillDownTarget, cardIdx: number) {
  const pointValues = drillPoints.value[cardIdx];
  if (!pointValues) return;
  const sourceWidgetCode = (props.chartLine as any).widgetCode;
  const click = buildDrillDownClick(target, pointValues, drillFilterMetadata.value,
    sourceWidgetCode, widgetId);
  if (click) emit('drillDownRequested', click);
}

function selectDrillTarget(target: BIDrillDownTarget) {
  if (!menu.value) return;
  triggerDrillDown(target, menu.value.cardIdx);
  menu.value = undefined;
}

function selectLink(link: BILinkTarget) {
  if (!menu.value) return;
  const pv = menuLinkPointValues.value[link.key];
  if (pv) executeLink(link, pv);
  menu.value = undefined;
}

function selectDrillByDimension(dim: BIDrillDownByDimension) {
  if (!menu.value) return;
  const sourceWidgetCode = (props.chartLine as any).widgetCode;
  const click = buildDrillDownByClick(dim, drillByData.value, menu.value.cardIdx,
    sourceWidgetCode, widgetId, props.dashboardId);
  if (click) emit('drillDownByRequested', click);
  menu.value = undefined;
}

function canShowIcon(card: EnhancedCardItem): boolean {
  return ObjectChecker.isNotEmptyOrNull(card.icon?.code);
}

function canShowSubtitle(card: EnhancedCardItem): boolean {
  return ObjectChecker.isNotEmptyOrNull(subtitleText(card));
}

function canShowBadge(card: EnhancedCardItem): boolean {
  if (!card.badge) return false;
  return ObjectChecker.isNotEmptyOrNull(badgeDisplay(card)) || ObjectChecker.isNotEmptyOrNull(card.badge.prefixIcon);
}

function canShowSparkline(card: EnhancedCardItem): boolean {
  return !!card.sparkline?.data?.length;
}

const viewOptionsStore = useDashboardViewOptionsStore();
const isAdmin = computed(() => LoginUtils.isCurrentUserAdminOrTreatAsAdmin());
const showAdminToolbox = computed(() => isAdmin.value && viewOptionsStore.showToolbox);

function openWidgetEditView() {
  if (!widgetId) return;
  const link = PlaceUtils.editLink('DashBoardWidget', widgetId);
  if (link) window.open(link, '_blank');
}
</script>

<template>
  <div :key="chartKey + widgetId"
       class="tw-h-full tw-w-full tw-overflow-y-auto dark:tw-bg-namaBackground-bright-dark tw-bg-namaBackground-bright-light tw-relative"
       :class="{'bi-widget-source': isFilterSource, 'bi-widget-filtered': isFilteredByOther}">
    <div v-if="isFilterSource || isFilteredByOther"
         class="tw-absolute tw-top-1 tw-end-1 tw-z-10 tw-flex tw-items-center tw-gap-1">
      <div v-if="isFilterSource"
           class="tw-flex tw-items-center tw-px-1.5 tw-py-0.5 tw-rounded tw-bg-primary tw-text-white tw-shadow"
           :title="emittingTooltip">
        <q-icon name="filter_alt" size="14px" class="emit-funnel"/>
      </div>
      <div v-if="isFilteredByOther"
           class="tw-flex tw-items-center tw-px-1.5 tw-py-0.5 tw-rounded tw-bg-info tw-text-white tw-shadow"
           :title="filteredByTooltip">
        <q-icon name="filter_alt" size="14px"/>
      </div>
    </div>
    <a v-if="showAdminToolbox"
       class="tw-absolute tw-z-20 tw-bg-gray-200 active:tw-bg-gray-300 dark:tw-bg-gray-700 dark:active:tw-bg-gray-600 tw-p-1 tw-rounded-lg tw-cursor-pointer tw-shadow"
       :class="(isFilterSource || isFilteredByOther) ? 'tw-top-7' : 'tw-top-1'"
       style="inset-inline-end: 0.25rem;"
       :title="'Open widget edit screen (admin)'"
       @click.stop="openWidgetEditView">
      <q-icon name="open_in_new" size="18px"/>
    </a>
    <div v-if="heading || (runtimeSelectorInfo && props.chartLine.widgetId)"
         class="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-px-2 tw-pt-2">
      <span v-if="heading"
            v-text="heading"
            class="tw-font-bold tw-text-lg dark:tw-text-white"/>
      <div class="tw-flex tw-items-center tw-gap-1 tw-shrink-0">
        <WizardSlotSelectorButton v-if="runtimeSelectorInfo && props.chartLine.widgetId"
                                  :widget-id="props.chartLine.widgetId"
                                  :info="runtimeSelectorInfo"
                                  @applied="onSlotOverrideChanged"
                                  @reset="onSlotOverrideChanged"/>
      </div>
    </div>
    <div class="tw-p-2 tw-bg-namaBackground-dim-light dark:tw-bg-namaBackground-dim-dark" :style="containerStyle">
      <div
        v-for="(card, cardIdx) in cards"
        :key="cardIdx"
        data-animate-box
        class="mobile:tw-w-fit mobile:tw-grow tw-relative tw-overflow-hidden tw-ring-1 tw-ring-black/10 dark:tw-ring-white/10 dark:tw-bg-namaBackground-bright-dark tw-bg-namaBackground-bright-light"
        :class="{'tw-cursor-pointer': isCardClickable}"
        :style="{
          minWidth: (layout.minCardWidth ?? 220) + 'px',
          flex: layout.direction === 'grid' || layout.columnsPerRow ? undefined : '1 1 ' + (layout.minCardWidth ?? 220) + 'px',
          ...cardStyleFor(card),
        }"
        @click="onCardClick(cardIdx, $event)"
        @contextmenu="openMenu(cardIdx, $event)">
        <button v-if="hasMenu"
                type="button"
                class="bi-card-kebab tw-absolute tw-top-1 tw-end-1 tw-rounded hover:tw-bg-black/10 dark:hover:tw-bg-white/10 tw-p-0.5"
                @click.stop.prevent="openMenu(cardIdx, $event)">
          <q-icon name="more_vert" size="16px"/>
        </button>
        <div class="tw-flex tw-flex-col tw-gap-1 tw-p-2 tw-h-full">
          <div class="tw-flex tw-items-start tw-justify-between tw-gap-2">
            <div class="tw-flex-1 tw-min-w-0">
              <p v-if="headerText(card)" v-text="headerText(card)"
                 class="tw-text-xs tw-font-medium tw-text-black/70 dark:tw-text-white/70"/>
              <p v-if="card.value" v-text="valueDisplay(card)"
                 :style="valueStyle(card)"
                 class="tw-text-2xl tw-font-semibold tw-leading-tight dark:tw-text-white"/>
            </div>
            <div v-if="canShowIcon(card)"
                 class="tw-shrink-0 tw-rounded-full tw-p-1.5 tw-flex tw-items-center tw-justify-center"
                 :style="iconStyle(card)" dir="ltr">
              <NamaIcon class="tw-size-4" :defaultIcon="card.icon!.code!"
                        :style="iconColorStyle(card)"/>
            </div>
          </div>
          <div v-if="canShowBadge(card)"
               class="tw-inline-flex tw-items-center tw-gap-1 tw-self-start tw-rounded-md tw-px-1.5 tw-py-0.5 tw-text-xs tw-font-medium"
               :style="badgeStyle(card)">
            <NamaIcon v-if="card.badge?.prefixIcon" :defaultIcon="card.badge.prefixIcon" class="tw-size-3"/>
            <span v-text="badgeDisplay(card)"/>
          </div>
          <p v-if="canShowSubtitle(card)" v-text="subtitleText(card)"
             class="tw-text-xs tw-text-black/60 dark:tw-text-white/60"/>
          <MiniSparkline v-if="canShowSparkline(card)" :config="card.sparkline!"/>
        </div>
      </div>
    </div>
    <BIActionMenu
      v-model="menuOpen"
      :targets="drillTargets"
      :point-values="menuPointValues"
      :filter-metadata="drillFilterMetadata"
      :drill-down-by-dimensions="drillByDimensions"
      :link-targets="linkTargets"
      :link-point-values="menuLinkPointValues"
      :x="menu?.x ?? 0"
      :y="menu?.y ?? 0"
      @drill-down-selected="selectDrillTarget"
      @drill-down-by-selected="selectDrillByDimension"
      @link-selected="selectLink"/>
  </div>
</template>

<style scoped>
.bi-widget-source {
  box-shadow: inset 0 0 0 2px var(--q-primary);
}

.bi-widget-filtered {
  box-shadow: inset 0 0 0 1px var(--q-info);
}

.bi-widget-source.bi-widget-filtered {
  box-shadow: inset 0 0 0 2px var(--q-primary), inset 0 0 0 4px var(--q-info);
}

.emit-funnel {
  transform: rotate(180deg);
}

.bi-card-kebab {
  z-index: 1;
}
</style>
